import { DocumentType } from "../types/Types";
import { isRabotenNalog, isPlanDoc } from "../documents/docTypeChecks";

/**
 * Canonical document-type → route-path builder.
 *
 * This is the SINGLE SOURCE OF TRUTH for turning a DocumentType (category /
 * subCategory / subType / code) into the URL used to navigate to it. It was
 * previously duplicated (and slightly divergent) across 6 sidebar components
 * (SidebarMaterial, SidebarNonMaterial, SidebarManufacturing,
 * SidebarStockManagement, SidebarFinancial, SidebarInventory).
 *
 * Every path produced here MUST match:
 *   1. An existing pattern in `ROUTE_CONFIG` (client/src/routes/routeConfig.tsx)
 *   2. A key in `ROUTE_TO_MODULE` (client/src/constants/modules.ts) so RBAC
 *      gating (ProtectedRoute / getModuleForPath) resolves correctly.
 *
 * Do not add a new branch here without verifying both of the above — a
 * mismatch means either a broken sidebar link or a fail-open RBAC gap.
 */
export const getDocumentPath = (doc: DocumentType): string => {
  // Work orders (manufacturing)
  if (isRabotenNalog(doc)) {
    return `/work-order/${doc.code}`;
  }

  // Documents explicitly assigned to the Sales menu use the sales screen
  // pattern even if their stored accounting category is material.
  if (doc.menuGroup === "sales") {
    return `/non-material/sales/${doc.code}`;
  }

  // Material documents
  if (doc.category === "material") {
    if (doc.subCategory && doc.subType) {
      // e.g. /material/issuance/external/3000, /material/reception/internal/7200
      return `/material/${doc.subCategory}/${doc.subType}/${doc.code}`;
    }
    if (doc.subCategory) {
      // e.g. /material/transfer/2026, /material/inventory-count/9A00,
      // /material/inventory-surplus/1A00, /material/inventory-shortage/3A00
      return `/material/${doc.subCategory}/${doc.code}`;
    }
    // Legacy/unassigned material docs (no subCategory) default to reception/external
    return `/material/reception/external/${doc.code}`;
  }

  // Non-material documents
  if (doc.category === "non-material") {
    if (doc.subCategory === "logistics" || doc.subCategory === "logistics-inbound") {
      return `/packing-list/${doc.code}`;
    }
    if (doc.subCategory === "invoice") {
      return `/invoice/${doc.code}`;
    }
    if (doc.subCategory === "procurement") {
      return `/procurement/${doc.code}`;
    }
    if (doc.subCategory) {
      // e.g. /non-material/sales/3Y00
      return `/non-material/${doc.subCategory}/${doc.code}`;
    }
    return `/non-material/sales/${doc.code}`;
  }

  // Planning documents (01 / 0206 / 1800) — normally built inline by
  // SidebarPlanning, kept here too for completeness/consistency.
  if (isPlanDoc(doc)) {
    return `/planning/${doc.code}`;
  }

  // Fallback — should not occur for any known document type
  return `/work-order/${doc.code}`;
};
